import type {ComponentProps, MouseEventHandler, ReactNode} from "react";
import {forwardRef, useMemo} from "react";
import RawTextarea from "react-textarea-autosize";
import {useGetNodeRefFromRef} from "@cdx/common";
import {DSIcon} from "../DSIcon/DSIcon";
import cx from "../../utils/cx";
import {Box, Col, css} from "../Box/Box";
import {DSInputStyles as styles} from "./DSInput.css";

export type DSBaseInput<T> = {
  onChange?: (value: T) => unknown;
  hasError?: boolean;
  size?: Exclude<keyof (typeof styles)["size"], "xxs">;
};

export type DSInputProps = Omit<JSX.IntrinsicElements["input"], "onChange" | "size"> &
  DSBaseInput<string>;

export const DSInput = forwardRef<HTMLInputElement, DSInputProps>((props, ref) => {
  const {onChange, type = "text", value, hasError, className, size = "md", ...rest} = props;
  return (
    <input
      type={type}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      value={value}
      className={cx(
        styles.theme[hasError ? "alert" : "default"],
        styles.layout.onlyInput,
        styles.size[size],
        className
        // !rest.disabled && Boolean(value) && styles.withValue
      )}
      ref={ref}
      {...rest}
    />
  );
});

export type DSInputWithPostfixProps = DSInputProps & {postfix: ReactNode; inputClassname?: string};
export const DSInputWithPostfix = forwardRef<HTMLInputElement, DSInputWithPostfixProps>(
  (props, passedRef) => {
    const {
      onChange,
      type = "text",
      value,
      hasError,
      className,
      inputClassname,
      size = "md",
      postfix,
      ...rest
    } = props;
    const {nodeRef, ref} = useGetNodeRefFromRef(passedRef);
    const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
      if (!nodeRef.current) return;
      if (e.target === nodeRef.current) return;
      nodeRef.current.focus();
    };
    return (
      <div
        onClick={handleClick}
        className={css(
          {sp: "8px", display: "flex", align: "baseline"},
          styles.theme[hasError ? "alert" : "default"],
          styles.layout.container,
          styles.size[size],
          className
        )}
      >
        <input
          type={type}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          value={value}
          className={cx(styles.inlineInput, inputClassname)}
          ref={ref}
          {...rest}
        />
        <Box color="secondary">{postfix}</Box>
      </div>
    );
  }
);

export type DSInputWithButtonProps = DSInputProps & {button: ReactNode; inputClassname?: string};
export const DSInputWithButton = forwardRef<HTMLInputElement, DSInputWithButtonProps>(
  (props, passedRef) => {
    const {
      onChange,
      type = "text",
      value,
      hasError,
      className,
      inputClassname,
      size = "md",
      button,
      ...rest
    } = props;
    const {nodeRef, ref} = useGetNodeRefFromRef(passedRef);
    const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
      if (!nodeRef.current) return;
      if (e.target === nodeRef.current) return;
      nodeRef.current.focus();
    };
    return (
      <div
        onClick={handleClick}
        className={css(
          {sp: "8px", display: "flex"},
          styles.theme[hasError ? "alert" : "default"],
          styles.layout.buttonContainer,
          styles.buttonInputSize[size],
          className
        )}
      >
        <input
          type={type}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          value={value}
          className={cx(styles.inlineInput, inputClassname)}
          ref={ref}
          {...rest}
        />
        {button}
      </div>
    );
  }
);

export type DSTextAreaProps = Omit<ComponentProps<typeof RawTextarea>, "onChange" | "size"> &
  DSBaseInput<string>;

export const DSTextArea = forwardRef<HTMLTextAreaElement, DSTextAreaProps>((props, ref) => {
  const {onChange, value, hasError, className, size = "md", minRows = 1, ...rest} = props;
  return (
    <RawTextarea
      ref={ref as any}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      value={value}
      className={cx(
        styles.theme[hasError ? "alert" : "default"],
        styles.layout.onlyInput,
        styles.textarea,
        styles.size[size],
        className
      )}
      minRows={minRows}
      cacheMeasurements
      {...rest}
    />
  );
});

export type DSSelectProps<T> = Omit<
  JSX.IntrinsicElements["select"],
  "onChange" | "size" | "value"
> &
  DSBaseInput<T> & {options: {value: T; label: string}[]; value?: T};

function toSelectValue<T>(value: T): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

export const DSSelect = forwardRef<HTMLSelectElement, DSSelectProps<any>>((props, ref) => {
  const {onChange, value, hasError, options, className, size = "md", ...rest} = props;
  const transformedOptions = useMemo(
    () =>
      options.map((o) => ({
        label: o.label ?? o.value,
        orgValue: o.value,
        stringValue: toSelectValue(o.value),
      })),

    [options]
  );
  return (
    <div
      className={css(
        {position: "relative"},
        styles.theme[hasError ? "alert" : "default"]
        // !rest.disabled && Boolean(value) && styles.withValue,
      )}
    >
      <select
        onChange={
          onChange
            ? (e) =>
                onChange(transformedOptions.find((o) => o.stringValue === e.target.value)!.orgValue)
            : undefined
        }
        value={toSelectValue(value)}
        className={cx(className, styles.layout.onlyInput, styles.select, styles.selectSize[size])}
        ref={ref}
        {...rest}
      >
        {transformedOptions.map(({label, stringValue}) => (
          <option key={stringValue} value={stringValue}>
            {label}
          </option>
        ))}
      </select>
      <Col
        absolute
        top="0"
        bottom="0"
        right="0"
        justify="center"
        pr={size === "lg" ? "8px" : "6px"}
        pointerEvents="none"
      >
        <DSIcon
          name="ChevronDown"
          label={null}
          size={size === "lg" ? 24 : 16}
          className={styles.selectIcon}
        />
      </Col>
    </div>
  );
});
